<template>
	<SideNavLayout>
		<section class="analyses-page">
			<div class="page-header">
				<h1>{{ t("comparableAnalysis.titlePlural") }}</h1>
				<p v-if="store.canRead" class="subtitle">{{ t("comparableAnalysis.startHelp") }}</p>
			</div>
			<p v-if="!store.canRead" class="empty-state" role="alert">{{ t("comparableAnalysis.noAccess") }}</p>
			<template v-else>
				<div v-if="error" class="empty-state" role="alert">
					<p>{{ error }}</p>
					<Button variant="primary" size="xs" @click="load">{{ t("comparableAnalysis.reload") }}</Button>
				</div>
				<SkeletonRows v-else-if="loading" role="status" :aria-label="t('comparableAnalysis.loading')" />
				<p v-else-if="!items.length" class="empty-state">{{ t("comparableAnalysis.empty") }}</p>
				<div v-else class="table-wrapper">
					<div class="table-scroll">
						<table>
							<thead>
								<tr>
									<th>{{ t("comparableAnalysis.columns.name") }}</th>
									<th class="col-date">{{ t("comparableAnalysis.columns.createdAt") }}</th>
									<th class="col-date">{{ t("comparableAnalysis.columns.updatedAt") }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="item in items" :key="item.id" @click="open(item.id)">
									<td class="col-name">
										<RouterLink :to="{ name: Route.ComparableAnalysis, params: { id: item.id } }" @click.stop>{{
											item.name
										}}</RouterLink>
									</td>
									<td class="col-date">{{ formatDate(item.createdAt) }}</td>
									<td class="col-date">{{ formatDate(item.updatedAt) }}</td>
								</tr>
							</tbody>
						</table>
					</div>
					<footer v-if="total > pageSize" class="pagination-footer">
						<Pagination
							:page="page"
							:count="total"
							:page-size="pageSize"
							:aria-label="t('comparableAnalysis.pagination')"
							@update:page="value => (offset = (value - 1) * pageSize)"
						/>
					</footer>
				</div>
			</template>
		</section>
	</SideNavLayout>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import type { ComparableAnalysisSummary } from "@/graphql"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import Button from "@/components/ui/Button.vue"
import Pagination from "@/components/ui/Pagination.vue"
import SkeletonRows from "@/components/ui/SkeletonRows.vue"
import { Route } from "@/utils/routing"
import { useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"
const store = useComparableAnalysisStore()
const router = useRouter()
const { t, locale } = useI18n()
const items = ref<ComparableAnalysisSummary[]>([])
const total = ref(0),
	offset = ref(0),
	pageSize = 25
const loading = ref(false),
	error = ref("")
const page = computed(() => Math.floor(offset.value / pageSize) + 1)
const dateFormat = computed(
	() =>
		new Intl.DateTimeFormat(locale.value === "en" ? "en-CA" : "fr-CA", {
			day: "numeric",
			month: "short",
			year: "numeric",
		})
)
function formatDate(value: string): string {
	return dateFormat.value.format(new Date(value))
}
function open(id: string) {
	void router.push({ name: Route.ComparableAnalysis, params: { id } })
}
let revision = 0
async function load() {
	const request = ++revision
	items.value = []
	error.value = ""
	if (!store.canRead) return
	loading.value = true
	try {
		const result = await store.list(pageSize, offset.value)
		if (request === revision) {
			items.value = result.items
			total.value = result.total
		}
	} catch (failure) {
		if (request === revision) error.value = failure instanceof Error ? failure.message : t("comparableAnalysis.error")
	} finally {
		if (request === revision) loading.value = false
	}
}
watch(() => [store.canRead, offset.value], load, { immediate: true })
</script>
<style scoped>
.analyses-page {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
	height: 100%;
	min-height: 0;
	box-sizing: border-box;
	padding: var(--spacing-xl);
	font-family: var(--font-family-primary);
	.page-header {
		h1 {
			margin: 0;
			font-size: var(--font-size-xl);
			font-weight: var(--font-weight-semibold);
			color: var(--neutral-900);
		}
		.subtitle {
			margin: var(--spacing-xs) 0 0;
			font-size: var(--font-size-small);
			color: var(--neutral-600);
		}
	}
	.table-wrapper {
		display: flex;
		flex-direction: column;
		flex: 0 1 auto;
		min-height: 0;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
		overflow: hidden;
		background: var(--neutral-0);
	}
	.table-scroll {
		flex: 1;
		min-height: 0;
		overflow: auto;
	}
	table {
		width: 100%;
		border-collapse: collapse;
		th {
			position: sticky;
			top: 0;
			z-index: 1;
			padding: var(--spacing-sm) var(--spacing-md);
			text-align: left;
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			letter-spacing: 0.02em;
			text-transform: uppercase;
			color: var(--neutral-600);
			background: var(--neutral-50);
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
		}
		tbody tr {
			cursor: pointer;
			&:not(:last-child) td {
				border-bottom: var(--border-width-thin) solid var(--neutral-200);
			}
			&:hover td {
				background: var(--neutral-50);
			}
		}
		td {
			padding: var(--spacing-smd) var(--spacing-md);
			font-size: var(--font-size-regular);
			color: var(--neutral-900);
		}
		.col-name a {
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
			text-decoration: none;
			&:hover {
				color: var(--primary-green);
			}
		}
		.col-date {
			width: var(--space-160);
			white-space: nowrap;
		}
		td.col-date {
			font-size: var(--font-size-small);
			color: var(--neutral-600);
		}
	}
	.pagination-footer {
		display: flex;
		justify-content: flex-end;
		padding: var(--spacing-sm) var(--spacing-md);
		border-top: var(--border-width-thin) solid var(--neutral-200);
	}
	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-sm);
		margin: 0;
		padding: var(--spacing-2xl);
		text-align: center;
		color: var(--neutral-600);
		p {
			margin: 0;
		}
	}
	@media (max-width: 768px) {
		padding: var(--spacing-md);
		gap: var(--spacing-md);
	}
}
</style>
